import app, { authReady } from '../lib/firebase'
import { getFirestore, runTransaction, doc } from 'firebase/firestore'
import type { Flashcard, ReviewRating } from '../../../shared/types'
import { calculateNextReview, calculateNextReviewDate } from '../lib/srs'

export async function submitReviewDirectToFirestore(
  docId: string,
  card: Flashcard,
  rating: ReviewRating,
  timeToAnswerMs?: number
): Promise<Flashcard> {
  // Ensure the client is authenticated before attempting a write
  try { await authReady } catch {}
  const db = getFirestore(app)
  const ref = doc(db, 'flashcards', docId)

  const updated: any = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref)
    if (!snap.exists()) throw new Error('Flashcard not found')
    const data = snap.data() as any

    const srs = calculateNextReview({
      rating,
      currentEaseFactor: data.easeFactor ?? card.easeFactor,
      currentIntervalDays: data.intervalDays ?? card.intervalDays,
      currentRepetitions: data.repetitions ?? card.repetitions,
      currentMasteryLevel: data.masteryLevel ?? card.masteryLevel,
    })
    const nextDate = calculateNextReviewDate(srs.intervalDays)

    const prevTotal = typeof data.totalReviews === 'number' ? data.totalReviews : 0
    const prevCorrect = typeof data.correctReviews === 'number' ? data.correctReviews : 0
    const isCorrect = rating !== 'again'
    const newTotal = prevTotal + 1
    const newCorrect = prevCorrect + (isCorrect ? 1 : 0)
    const accuracy = Math.round((newCorrect / newTotal) * 100)

    tx.update(ref, {
      masteryLevel: srs.masteryLevel,
      easeFactor: srs.easeFactor,
      intervalDays: srs.intervalDays,
      repetitions: srs.repetitions,
      nextReviewDate: nextDate,
      lastReviewDate: new Date().toISOString().split('T')[0],
      totalReviews: newTotal,
      correctReviews: newCorrect,
      accuracy,
      _lastClientReviewAt: Date.now(),
      _lastClientLatencyMs: timeToAnswerMs ?? null,
    } as any)

    return {
      ...(card as any),
      masteryLevel: srs.masteryLevel,
      easeFactor: srs.easeFactor,
      intervalDays: srs.intervalDays,
      repetitions: srs.repetitions,
      nextReviewDate: nextDate,
      lastReviewDate: new Date().toISOString().split('T')[0],
      totalReviews: newTotal,
      correctReviews: newCorrect,
      accuracy,
      _docId: docId,
    }
  })

  return updated as Flashcard
}
